<template>
  <v-dialog
    v-model="isModalActive"
    persistent
    scrollable
    width="80vw"
    max-width="80vw"
  >
    <v-card v-if="form == null">
      <v-card-text :class="contentStyles">
        <v-layout align-center justify-center fill-height>
          <v-progress-circular
            :size="50"
            style="text-align: center;"
            color="primary"
            indeterminate
          />
        </v-layout>
      </v-card-text>
    </v-card>
    <v-card v-else>
      <v-card-text>
        <v-stepper v-model="step">
          <CampaignModalNav
            :step="step"
            :steps="steps"
            :max-step="maxStep"
            :editable="insertionOrder.canEditCampaign"
            :is-narrow="isNarrow"
            @change="changeStep"
          />
          <template v-if="isSimulationVisible">
            <CampaignModalSimulationResult
              v-show="isShowSimulation && form.campaign.hasGeoMenuSetting"
              :form="form"
              :step="step"
              :advertiser="advertiser"
              :min-u-u="minUU"
              @simulated="setUU"
            />
          </template>
          <v-stepper-items>
            <v-stepper-content
              v-for="(item, index) in steps"
              :key="item.name"
              :step="index + 1"
              :class="contentStyles"
            >
              <div
                ref="page"
                :is="`CampaignModalPage${item.name}`"
                v-if="stepInfo.name === item.name"
                :edit-mode="!(isNew || isDuplication)"
                :form="form"
                :advertiser="advertiser"
                :insertion-order="insertionOrder"
                :system-owner="systemOwner"
                :step="step"
                :maxStep="maxStep"
                :menuStep="menuStep"
                @back="back"
                @closeAndEditInsertionOrder="closeAndEditInsertionOrder"
                @close="$emit('input', false)"
                @importSimulation="onImportSimulation"
                @changeStep="changeStep"
                @resetMaxStep="maxStep = 1"
              />
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>

      <v-card-actions>
        <div
          v-if="steps[step - 1].manualTitle && steps[step - 1].manualUrl"
          style="display: flex; align-items: center; margin-left: 16px;"
        >
          <v-icon
            @click="openHelpPage(steps[step - 1].manualUrl)"
            color="primary"
            >mdi-help-circle</v-icon
          >
          <span
            @click="openHelpPage(steps[step - 1].manualUrl)"
            style="font-size: 16px; margin-left: 8px; cursor: pointer; color: #007bff; text-decoration:underline;"
          >
            {{ steps[step - 1].manualTitle }}
          </span>
        </div>
        <v-spacer />
        <AppButtonCancel :loading="loading" @click="onClickCancel">
          キャンセル
        </AppButtonCancel>
        <AppButtonOk
          v-if="step > 1 && insertionOrder.canEditCampaign"
          :loading="loading"
          @click="onClickPrev"
        >
          <v-icon large>mdi-menu-left</v-icon>
        </AppButtonOk>
        <AppButtonOk
          v-if="step < steps.length"
          :loading="loading"
          @click="onClickNext"
        >
          <v-icon large>mdi-menu-right</v-icon>
        </AppButtonOk>
        <AppButtonOk
          v-if="isLastStep && insertionOrder.canEditCampaign"
          :loading="loading"
          :disabled="isDisabled"
          @click="startSaveFlow"
          color="primary"
        >
          登録
        </AppButtonOk>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions, mapGetters } from 'vuex'
import { css } from 'emotion'
import { snackbarType } from '@/vuex/modules/ui/snackbar/constants'
import {
  DeprecatedOfficeListedOptions,
  MenuTypes,
} from '@/models/Menu/Constants'
import CampaignModalNav from './CampaignModalNav.vue'
import CampaignModalPageMenu from './CampaignModalPageMenu'
import CampaignModalPageBudget from './CampaignModalPageBudget'
import CampaignModalPageMansion from './CampaignModalPageMansion'
import CampaignModalPageGeo from './CampaignModalPageGeo'
import CampaignModalPageTarget from './CampaignModalPageTarget'
import CampaignModalPageAdvanced from './CampaignModalPageAdvanced'
import CampaignModalPageCreative from './CampaignModalPageCreative'
import CampaignModalPageCreativeCondition from './CampaignModalPageCreativeCondition'
import CampaignModalPageConfirm from './CampaignModalPageConfirm'
import CampaignModalSimulationResult from './CampaignModalSimulationResult'
import CampaignModalPageAutomobile from './CampaignModalPageAutomobile'
import CampaignModalPageNewspaper from './CampaignModalPageNewspaper'
import CampaignModalPageCustomGeo from './CampaignModalPageCustomGeo'
import CampaignModalPageRetargeting from './CampaignModalPageRetargeting'
import CampaignModalPagePTA from './CampaignModalPagePTA'
import CampaignModalPageCustom from './CampaignModalPageCustom'
import CampaignModalPageGeoGenome from './CampaignModalPageGeoGenome'
import CampaignModalPageOffice from './CampaignModalPageOffice'
import CampaignModalPageRailway from './CampaignModalPageRailway'
import {
  CampaignRepository,
  CreativeAttachTargets,
  Form,
} from '@/models/CampaignOperation'
import { SimulationPlanRepository } from '@/models/Simulation'
import LocationRepository from '@/models/Location/LocationRepository'
import LocationGroupRepository from '@/models/LocationGroup/LocationGroupRepository'
import { runInBatches } from '@/models/runInBatches'
import { RailwayTargetTypes } from '@/models/Menu/Constants'
import { locationGroupType } from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/constants'
import {
  CampaignBudgetFormModel,
  TemporaryCampaignBudgetFormModel,
} from '@/models/CampaignOperation/Form'
import { CreativeTypes } from '@/models/Creative'
import advertiserApi from '@/api/AdvertiserApi'

const BREAK_POINT = 1160 // これ以下は狭い画面とみなす
const MIN_UU = 0
const BATCH_SIZE = 10

/* eslint vue/no-unused-components: 0 */
export default {
  name: 'CampaignModal',
  components: {
    CampaignModalNav,
    CampaignModalPageMenu,
    CampaignModalPageBudget,
    CampaignModalPageMansion,
    CampaignModalPageGeo,
    CampaignModalPageOffice,
    CampaignModalPageTarget,
    CampaignModalPageAdvanced,
    CampaignModalPageCreative,
    CampaignModalPageCreativeCondition,
    CampaignModalPageConfirm,
    CampaignModalSimulationResult,
    CampaignModalPageAutomobile,
    CampaignModalPageNewspaper,
    CampaignModalPageRetargeting,
    CampaignModalPageCustomGeo,
    CampaignModalPagePTA,
    CampaignModalPageGeoGenome,
    CampaignModalPageCustom,
    CampaignModalPageRailway,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    insertionOrder: {
      type: Object,
      required: true,
    },
    advertiser: {
      type: Object,
      required: true,
    },
    campaignSummary: {
      type: Object,
      default: null,
    },
    sourceCampaign: {
      type: Object,
      default: null,
    },
    systemOwner: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: null,
      currentStep: 1,
      maxStep: 1,
      loading: false,
      errMessage: '',
      campaignIdForChangeStatus: undefined,
      campaign: null,
      windowWidth: undefined,
      isRegistrableUU: false,
      minUU: MIN_UU,
    }
  },
  computed: {
    ...mapGetters('account', [
      'creativeVisible',
      'isValidateSimulation',
      'isSimulationVisible',
    ]),
    ...mapGetters('campaign', ['getLocationsById']),
    ...mapGetters('currentAdvertiser', ['currentAdvertiser']),
    userBelongType() {
      return this.$store.getters['account/belongType']
    },
    userBelongId() {
      return this.$store.getters['account/belongId']
    },
    isNew() {
      return this.campaignSummary == null && this.sourceCampaign == null
    },
    isDuplication() {
      return this.campaignSummary == null && this.sourceCampaign != null
    },
    isModalActive: {
      get() {
        return this.value
      },
      set(isActive) {
        this.$emit('input', isActive)
      },
    },
    step: {
      get() {
        return this.insertionOrder.canEditCampaign
          ? this.currentStep
          : this.maxStep
      },
      set(stepNumber) {
        this.currentStep = stepNumber
        this.maxStep = Math.max(this.currentStep, this.maxStep)
      },
    },
    menuType() {
      if (!this.form || !this.form.campaign.menuId) return null
      return this.form.campaign.menu.menuType
    },
    menuSetting() {
      return this.form.campaign.menuSetting
    },
    steps() {
      const { name, title, manualTitle, manualUrl } = this.menuStep
      const hasTargetStep =
        this.form.campaign.hasGeoMenuSetting ||
        (this.form.campaign.hasRailwayMenuSetting &&
          this.menuSetting.orderDescription.targetTypes.includes(
            RailwayTargetTypes.Station,
          )) ||
        this.form.campaign.hasCustomGeoMenuSetting
      return [
        {
          name: 'Menu',
          title: 'メニュー選択',
          manualTitle: 'メニュー選択 ヘルプはこちら',
          manualUrl: 'https://go.geologic.co.jp/adminmanual_campaign_menu',
        },
        {
          name: 'Budget',
          title: '予算設定',
          manualTitle: '予算設定 ヘルプはこちら',
          manualUrl: 'https://go.geologic.co.jp/adminmanual_campaign_budget',
        },
        {
          name: name,
          title: title,
          manualTitle: manualTitle,
          manualUrl: manualUrl,
        },
        ...(hasTargetStep
          ? [
              {
                name: 'Target',
                title: '配信対象設定',
                manualTitle: '配信対象設定 ヘルプはこちら',
                manualUrl:
                  'https://go.geologic.co.jp/adminmanual_campaign_audience',
              },
            ]
          : []),
        {
          name: 'Advanced',
          title: '高度な設定',
          manualTitle: '高度な設定 ヘルプはこちら',
          manualUrl:
            'https://go.geologic.co.jp/adminmanual_campaign_advancedsetting',
        },
        {
          name: 'CreativeCondition',
          title: 'クリエイティブ設定条件',
          manualTitle: 'クリエイティブ設定条件 ヘルプはこちら',
          manualUrl: 'https://go.geologic.co.jp/adminmanual_campaign_creative',
        },
        {
          name: 'Creative',
          title: 'クリエイティブ設定',
          manualTitle: this.getCreativeManual().manualTitle,
          manualUrl: this.getCreativeManual().manualUrl,
        },
        {
          name: 'Confirm',
          title: '確認',
          manualTitle: 'キャンペーン 設定確認 ヘルプはこちら',
          manualUrl:
            'https://go.geologic.co.jp/adminmanual_campaign_confirmation',
        },
      ].filter(step => this.creativeVisible || !/Creative/.test(step.name))
    },
    menuStep() {
      switch (this.menuType) {
        case MenuTypes.Geo:
          return {
            name: 'Geo',
            title: '地点設定',
            manualTitle: '地点設定 ヘルプはこちら',
            manualUrl:
              'https://go.geologic.co.jp/adminmanual_campaign_location',
          }
        case MenuTypes.Newspaper:
          return { name: 'Newspaper', title: '新聞購読率条件設定' }
        case MenuTypes.Pta:
          return { name: 'PTA', title: 'PTA対象設定' }
        case MenuTypes.Automobile:
          return { name: 'Automobile', title: '対象自動車設定' }
        case MenuTypes.Mansion:
          return { name: 'Mansion', title: '対象マンション設定' }
        case MenuTypes.GeoGenome:
          return { name: 'GeoGenome', title: '対象クラスター設定' }
        case MenuTypes.Railway:
          return { name: 'Railway', title: '路線・駅設定' }
        case MenuTypes.Retargeting:
          return { name: 'Retargeting', title: 'タグ情報設定' }
        case MenuTypes.CustomGeo:
          return { name: 'CustomGeo', title: '配信エリア指定' }
        case MenuTypes.Office:
          return { name: 'Office', title: '対象オフィス設定' }
        case MenuTypes.Custom:
          return { name: 'Custom', title: 'ターゲティング設定' }
        default:
          return {}
      }
    },
    isLastStep() {
      return this.step === this.steps.length
    },
    stepInfo() {
      return this.steps[this.step - 1]
    },
    creativeConditionStep() {
      return this.steps.length - 1
    },
    contentStyles() {
      return css`
        min-height: calc(90vh - 156px);
      `
    },
    isNarrow() {
      return this.windowWidth < BREAK_POINT
    },
    isShowSimulation() {
      // メニューが選択されていること
      if (this.form.campaign.isMenuEmpty) {
        return false
      }
      // インフィードの場合、system ownerであること
      if (this.form.campaign.creativeType === CreativeTypes.INFEED) {
        return this.userBelongType === 'system owner' && this.userBelongId === 1
      }
      // シミュレーション可能なクリエイティブタイプが選択されていること
      return this.form.isSimulatableCreativeType()
    },
    isDisabled() {
      return false
      // if (!this.isSimulationVisible || !this.isValidateSimulation) {
      //   return false
      // }
      // // 非公開の地点グループが含まれている場合はUU数チェックを行わない
      // if (this.menuSetting.hasPrivateGroup()) {
      //   return false
      // }
      // // シミュレーション不可能なクリエイティブを選択した場合はUU数チェックを行わない
      // if (!this.form.isSimulatableCreativeType()) {
      //   return false
      // }
      // return !this.isRegistrableUU
    },
    hasSimulationLocationGroup() {
      if (!this.form.campaign.hasGeoMenuSetting) {
        return false
      }
      return this.menuSetting.deliveryLocationGroups.some(
        group => group.isSimulation === true,
      )
    },
  },
  mounted() {
    this.init()
    this.setWindowWidth()
    this.watchWindowResize()
  },
  beforeDestroy() {
    this.unwatchWindowResize()
  },
  methods: {
    ...mapActions('snackbar', {
      showSnackbar: 'SHOW_SNACKBAR',
    }),
    ...mapActions('campaign', {
      setSimulationLocationGroups: 'SET_SIMULATION_LOCATION_GROUPS',
    }),
    async init() {
      if (this.isNew) {
        this.form = await Form.factoryCreateForm(
          this.systemOwner,
          this.advertiser,
          this.insertionOrder,
          this.creativeVisible,
        )
        this.resetCampaignBudgets()
      } else if (this.isDuplication) {
        const sourceCampaign = await CampaignRepository.get(
          this.sourceCampaign.id,
        )
        this.form = await Form.factoryCreateFormFromCampaign(
          sourceCampaign,
          this.systemOwner,
          this.advertiser,
          this.insertionOrder,
          this.creativeVisible,
        )
      } else {
        this.campaign = await CampaignRepository.get(this.campaignSummary.id)

        this.form = await Form.factoryUpdateForm(
          this.campaign,
          this.systemOwner,
          this.advertiser,
          this.insertionOrder,
          this.creativeVisible,
        )
        this.maxStep = this.steps.length

        // menuTypeがOfficeの場合、廃止された上場区分がlistingCategoryClassificationTargetsに含まれていればモーダルを出してステップを移動させる
        if (
          this.campaign.campaign.menuType === MenuTypes.Office &&
          this.campaign.menuSetting.orderDescription.listingCategoryClassificationTarget.listingCategoryClassificationTargets.some(
            listingCategoryClassificationTarget =>
              DeprecatedOfficeListedOptions.some(
                deprecatedOfficeListedOption =>
                  deprecatedOfficeListedOption.key ===
                  listingCategoryClassificationTarget,
              ),
          )
        ) {
          const result = await this.$confirm(
            '廃止された上場区分が設定されています。上場区分を再設定してください。',
            {
              onlyOk: true,
              okText: '編集',
            },
          )
          if (result) {
            this.campaign.menuSetting.orderDescription.listingCategoryClassificationTarget.listingCategoryClassificationTargets = []
            this.currentStep = 3
            return
          }
        }

        // 編集時は最初から確認画面を見せる
        this.currentStep = this.maxStep
      }
    },
    async onImportSimulation(hashId) {
      if (!hashId) {
        this.showImportFailureSnackBar()
        return false
      }
      const simulationPlanRes = await SimulationPlanRepository.findByHashId(
        hashId,
      )

      const isLegacySimulation =
        simulationPlanRes.simulationPlan.simulationPlanMenu === undefined

      if (isLegacySimulation) {
        await this.$confirm(
          `本シミュレーションは、無効状態となっております。
          共有元のシミュレーションについて、メニューを再度選択し上書き保存していただくようお願いいたします。`,
          {
            onlyOk: true,
            okText: '閉じる',
          },
        )
        return
      }

      if (!this.form.isNew) {
        await this.showImportAlert(simulationPlanRes)
      }

      const { simulationPlanMenu } = simulationPlanRes.simulationPlan

      const targetMenu = this.form.menus.find(
        m => m.id === simulationPlanMenu.originOwnerProfileMenuId,
      )

      if (targetMenu === undefined) {
        await this.$confirm(
          'このメニューは使用できません。メニューを再選択してください。',
          {
            onlyOk: true,
            okText: '閉じる',
          },
        )
        return
      }

      const formFromSimulation = await this.factoryFromFromSimulation({
        ...simulationPlanRes,
        ownerProfileMenu: {
          id: targetMenu.id,
          name: simulationPlanMenu.menuName,
          creativeType: targetMenu.creativeType,
          menuType: simulationPlanMenu.menuType,
          unitType: simulationPlanMenu.unitType,
          unitPrice: simulationPlanMenu.grossPrice,
          costPlusMarginPercentage: simulationPlanMenu.costPlusMarginPercentage,
        },
      })

      this.form = formFromSimulation

      /** シミュレーション地点情報をStoreに格納する */
      const locationGroups = {
        customLocationGroups:
          simulationPlanRes.menuSetting.simulationCustomLocationGroups,
        poiLocationGroups:
          simulationPlanRes.menuSetting.simulationPoiLocationGroups,
      }
      await this.setSimulationLocationGroups(locationGroups)
      this.showImportSuccessSnackBar()
    },
    async factoryFromFromSimulation(simulationPlan) {
      if (this.form.isNew) {
        return Form.factoryCreateFormFromSimulation(
          simulationPlan,
          this.systemOwner,
          this.advertiser,
          this.insertionOrder,
          this.creativeVisible,
        )
      }
      return Form.factoryUpdateFormFromSimulation(
        this.form,
        this.menuSetting,
        simulationPlan,
      )
    },
    async showImportAlert({ simulationPlan }) {
      const baseMessage =
        'シミュレーションに設定されている配信時間帯設定が引き継がれております。' +
        '配信時間帯設定をされている場合、高度な設定の配信曜日時間帯設定をご確認ください。'

      const menuMessage =
        '本キャンペーンで設定されているメニューは、シミュレーションで設定されているメニューとは異なります。' +
        '単価が異なる場合がございますので、ご注意ください。'

      const body = this.isNotEqualMenu(this.form.campaign, simulationPlan)
        ? `${baseMessage}\n\n${menuMessage}`
        : baseMessage

      await this.$confirm(undefined, {
        onlyOk: true,
        okText: '閉じる',
        body,
      })
    },
    isNotEqualMenu(campaign, simulationPlan) {
      return (
        campaign.menuId !==
        simulationPlan.simulationPlanMenu.originOwnerProfileMenuId
      )
    },
    async confirmCancel() {
      const result = await this.$confirm(
        'これまで設定した内容がすべて破棄されますがよろしいですか？',
      )
      return result
    },
    async onClickCancel() {
      const result = await this.confirmCancel()
      if (result) {
        this.step = 1
        this.$emit('input', false)
      }
    },
    onClickPrev() {
      this.changeStep(this.step - 1)
    },
    onClickNext() {
      this.changeStep(this.step + 1)
    },
    async changeStep(stepNumber) {
      const [$page] = this.$refs.page
      // maxStep 表示中はバリデーションをしない
      // ただし先に進む場合はバリデーションを行う。
      if (this.step < this.maxStep || this.maxStep < stepNumber) {
        const isValid = await this.validate($page.errorMessage)
        if (!isValid) {
          return
        }
        const isInfoValid = await this.infoMessageValidation($page.infoMessage)
        if (!isInfoValid) {
          this.stepAction(this.creativeConditionStep)
          return
        }
      }
      // maxStep より先に進む場合は確認メッセージ表示
      if (this.maxStep < stepNumber) {
        const confirmed = await this.confirm($page.confirmMessage)
        if (!confirmed) {
          return
        }
      }
      this.stepAction(stepNumber)
    },
    stepAction(stepNumber) {
      this.step = stepNumber
      this.scrollToTop()
    },
    back(backToName) {
      let backToStep = this.step - 1
      this.steps.some((step, index) => {
        if (step.name === backToName) {
          backToStep = index + 1
          return true
        }
      })
      this.stepAction(backToStep)
    },
    scrollToTop() {
      this.$nextTick(() => {
        document.getElementsByClassName('v-dialog--persistent')[0].scrollTop = 0
      })
    },
    async validate(errorMessage) {
      const isArray = Array.isArray(errorMessage)
      if ((isArray && errorMessage.length !== 0) || errorMessage !== '') {
        await this.$confirm('入力エラー', {
          okText: '閉じる',
          onlyOk: true,
          body: isArray ? errorMessage.join('\n') : errorMessage,
        })
        return false
      }
      return true
    },
    async infoMessageValidation(infoMessage) {
      if (infoMessage) {
        const result = await this.$confirm('注意', {
          okText: '閉じる',
          cancelText: 'クリエイティブ設定へ',
          body: infoMessage,
        })
        if (!result) return false
      }
      return true
    },
    async confirm(confirmMessage) {
      if (confirmMessage != null && confirmMessage !== '') {
        const confirmed = await this.$confirm('確認', {
          onlyOk: false,
          body: confirmMessage,
        })
        if (!confirmed) return false
      }
      return true
    },
    async closeAndEditInsertionOrder() {
      const result = await this.confirmCancel()
      if (result) {
        this.isModalActive = false
        this.$emit('editInsertionOrder')
      }
    },
    classifyGroups(groups) {
      return groups.reduce(
        (acc, group) => {
          // シミュレーション地点グループ && 選ばれてないものはskip
          if (group.isSimulation && group.deleted) return acc
          if (group.isSimulation && !group.deleted) {
            acc.simulationLocationGroups.push(group)
            return acc
          }
          acc.locationGroups.push(group)
          return acc
        },
        { locationGroups: [], simulationLocationGroups: [] },
      )
    },
    assignNewItemToLocation(location) {
      return {
        ...location,
        newItem: {
          new: true,
        },
      }
    },
    async registerLocationGroups(locationGroups) {
      const ownerProfileId = this.insertionOrder.ownerProfileId
      try {
        return runInBatches(
          locationGroups.map(group => () =>
            LocationGroupRepository.create({
              ownerProfileId,
              locationType: group.locationType,
              name: group.name,
              accessibleType: 'public',
            }),
          ),
          BATCH_SIZE,
        )
      } catch (error) {
        const registeredGroups = error.results
        await this.deleteLocationGroups(registeredGroups)
        throw error
      }
    },
    async registerLocations(locationGroups, locationGroupWithPoints) {
      return runInBatches(
        locationGroups.map((group, i) => () => {
          switch (locationGroupWithPoints[i].locationType) {
            case locationGroupType.CUSTOM_LOCATION: {
              const locations = locationGroupWithPoints[i].locations
              return LocationRepository.saveBulkCustomLocations(
                group.id,
                locations,
              )
            }
            case locationGroupType.POI_LOCATION: {
              return LocationRepository.savePoiLocation(
                group.id,
                locationGroupWithPoints[i].categoryId,
                locationGroupWithPoints[i].conditions,
                locationGroupWithPoints[i].administrativeDistrictType,
                locationGroupWithPoints[i].area,
              )
            }
          }
        }),
        BATCH_SIZE,
      )
    },
    async deleteLocationGroups(groups) {
      await runInBatches(
        groups.map(group => () => LocationGroupRepository.delete(group)),
        BATCH_SIZE,
      )
    },
    assignLocations(locationGroups) {
      return locationGroups.map(group => {
        const locations = this.getLocationsById(group.locationGroupId).map(
          this.assignNewItemToLocation,
        )
        return {
          ...group,
          locations,
        }
      })
    },
    async saveLocationGroups() {
      if (!this.hasSimulationLocationGroup) {
        return []
      }
      // 正地点グループとしシミュレーション地点グループを分ける
      const { locationGroups, simulationLocationGroups } = this.classifyGroups(
        this.menuSetting.deliveryLocationGroups,
      )

      // 選択したシミュレーション地点グループに地点情報を割り当てる
      const locationGroupWithPoints = this.assignLocations(
        simulationLocationGroups,
      )

      // シミュレーション地点グループを正地点グループに登録
      const registeredLocationGroups = await this.registerLocationGroups(
        simulationLocationGroups,
      ).catch(error => {
        throw error
      })

      // シミュレーション地点を正地点に登録
      try {
        await this.registerLocations(
          registeredLocationGroups,
          locationGroupWithPoints,
        )
      } catch (error) {
        // 地点登録に失敗した場合は登録した地点グループを削除する
        await this.deleteLocationGroups(registeredLocationGroups)
        throw error
      }

      // シミュレーションから読み込んだ地点グループに正地点グループのIDに振り直す
      const newLocationGroups = simulationLocationGroups.map((group, i) => {
        const locationGroupId = registeredLocationGroups[i].id
        delete group.id

        return {
          ...group,
          locationGroupId,
        }
      })

      // 正地点グループになったシミュレーション地点グループをFormにマージする
      this.menuSetting.deliveryLocationGroups = [
        ...locationGroups,
        ...newLocationGroups,
      ]

      return registeredLocationGroups
    },
    async startSaveFlow() {
      let isError = false
      const result = this.isCreativeScheduleViolation()
      if (result) {
        await this.$confirm('入力エラー', {
          okText: 'クリエイティブ設定へ',
          onlyOk: true,
          body: `クリエイティブ配信スケジュールがキャンペーン予算期間外に設定されています。
            クリエイティブ設定から修正してください。`,
        })
        this.stepAction(this.creativeConditionStep)
        return
      }

      try {
        await this.confirmCampaignBudget()
        await this.confirmMenu()

        this.loading = true

        const savedLocationGroups = await this.saveLocationGroups()

        const result = await this.form.save().catch(async error => {
          // キャンペーン登録失敗時に新規追加した地点グループをロールバックする
          if (savedLocationGroups.length > 0) {
            await this.deleteLocationGroups(savedLocationGroups)
          }
          throw error
        })
        this.campaignIdForChangeStatus = result.campaign.id
        this.loading = false

        if (this.isNew || this.isDuplication) {
          await this.confirmSave()
          if (await this.isFirstCampaign()) {
            await this.confirmReportSetting()
          }
        }

        this.$emit('save')
        if (!this.isNew && !this.isDuplication) {
          await this.confirmEditCompleted()
        }
      } catch (error) {
        await this.confirmFailure()
        isError = true
      } finally {
        if (isError) this.loading = false
        else this.isModalActive = false
      }
    },
    isCreativeScheduleViolation() {
      if (!this.form.campaign.useCreativeSchedule) {
        return false
      }
      return this.form.campaign.mergedCampaignBudgets.some(
        cb => cb.isNotInCampaignBudgetPeriod,
      )
    },
    async confirmCampaignBudget() {
      const expiredCampaignBudgets = this.form.campaign.expiredCampaignBudgets
      if (expiredCampaignBudgets.length === 0) return

      const expiredCampaignBudgetMessages = expiredCampaignBudgets
        .map(budget => {
          return (
            `・配信日：${budget.startTime} 〜 ${budget.endTime}\n` +
            `・上限予算：¥${budget.budget.toLocaleString()}\n`
          )
        })
        .join('\n')

      const confirm = `終了期間が既に過ぎているキャンペーン予算があります。\n
        ${expiredCampaignBudgetMessages}\n
        該当のキャンペーン予算の終了日時を${dayjs()
          .add(3, 'm')
          .format('YYYY-M-D HH:59')} 以降に設定してください。`

      await this.$confirm('確認', {
        onlyOk: true,
        body: confirm,
      })
      throw Promise.error
    },
    async confirmMenu() {
      const menu = this.form.campaign.menu
      if (this.form.outOfMenuPeriodViolation) {
        const confirm =
          '配信開始時間が、メニューの使用可能期間内ではありません。\n' +
          `・メニュー：${menu.startDate} 〜 ${menu.endDate}\n` +
          `・配信開始：${this.form.wholePeriodOfBudgets.startTime}`
        await this.$confirm('確認', {
          onlyOk: true,
          body: confirm,
        })
      }
    },
    async confirmSave() {
      const completeMessage = this.form.campaign.hasGeoMenuSetting
        ? '配信システムへの反映には、数時間〜1日程度時間を要します。'
        : '本メニューでは、設定の反映までに通常5営業日ほどいただいております。'
      const result = await this.$confirm('キャンペーンの設定が完了しました', {
        body: `${completeMessage}\nキャンペーンのステータスを設定してください。`,
        okText: '有効化',
        cancelText: '無効化',
      })
      const res = await this.form.changeStatus(
        this.campaignIdForChangeStatus,
        result,
      )
      return res
    },
    async confirmEditCompleted() {
      const body = this.form.campaign.hasGeoMenuSetting
        ? '配信システムへの反映には、数時間〜1日程度時間を要します。'
        : '本メニューでは、設定の反映までに通常5営業日ほどいただいております。\n※ 設定内容によっては、それ以上かかる場合もございます。'
      await this.$confirm('キャンペーンの編集が完了しました', {
        body: body,
        okText: '閉じる',
        onlyOk: true,
      })
    },
    confirmFailure() {
      const messages = ['保存に失敗しました']
      if (this.hasSimulationLocationGroup) {
        messages.push('再度シミュレーションを読み込んで入稿してください。')
      }
      const message = messages.join('\n')
      return this.$confirm(message, {
        body: `解決しない場合サポートにお問い合わせください。
                <a href="https://www.geologic.co.jp/inquiry" target="_blank" rel="noopener">https://www.geologic.co.jp/inquiry</a>`,
        okText: '閉じる',
        onlyOk: true,
      })
    },
    async confirmReportSetting() {
      const isSettingReport = await this.$confirm('レポート設定確認', {
        body: `現在設定中の案件のレポートについて、定期配信メール設定をしますか？`,
        okText: 'レポート設定へ',
        cancelText: '閉じる',
      })
      if (isSettingReport) {
        const source = 'campaignCreateButton'
        this.$sendEventToGA('buttonClick', {
          buttonName: 'intervalReportSettingModalOpenButton',
          source: source,
        })
        const insertionOrderId = this.form.insertionOrder.id
        const routerParams = {
          name: 'Report Interval Mail',
          query: {
            insertionOrderId: insertionOrderId,
            source: source,
          },
        }
        const resolvedRoute = this.$router.resolve(routerParams)
        window.open(resolvedRoute.href, '_blank', 'noreferrer')
      }
    },
    showImportSuccessSnackBar() {
      this.showSnackbar({
        type: snackbarType.SUCCESS,
        message: `シミュレーションを読み込みました`,
      })
    },
    showImportFailureSnackBar() {
      this.showSnackbar({
        type: snackbarType.ERROR,
        message: `シミュレーションを読み込めませんでした`,
      })
    },
    setWindowWidth() {
      this.windowWidth = window.innerWidth
    },
    watchWindowResize() {
      window.addEventListener('resize', this.setWindowWidth)
    },
    unwatchWindowResize() {
      window.removeEventListener('resize', this.setWindowWidth)
    },
    setUU(simulationResult) {
      const { uu } = simulationResult.simulation
      this.isRegistrableUU = uu >= this.minUU
    },
    resetCampaignBudgets() {
      if (!this.isNew) return

      this.form.campaign.campaignBudgets = this.resetCampaginBudgetsIfIsStartedAlready()
      this.form.campaign.temporaryCampaignBudgets = this.resetTemporaryCampaginBudgetsIfIsStartedAlready()
    },
    resetCampaginBudgetsIfIsStartedAlready() {
      return this.form.campaign.campaignBudgets.map(cb => {
        const isStartedAlready = this.form.insertionOrderBudgets
          .filter(iob => iob.id === cb.insertionOrderBudgetId)
          .some(iob => this.isAfterFromNow(iob.startTime))

        if (isStartedAlready) {
          return CampaignBudgetFormModel.assign({
            ...cb,
            budget: 0,
          })
        } else {
          return cb
        }
      })
    },
    resetTemporaryCampaginBudgetsIfIsStartedAlready() {
      return this.form.campaign.temporaryCampaignBudgets.map(cb => {
        const isStartedAlready = this.form.insertionOrderBudgets
          .filter(iob => iob.id === cb.insertionOrderBudgetId)
          .some(iob => {
            if (iob.startTime) {
              return this.isAfterFromNow(iob.startTime)
            }
            if (
              iob.latestRequest &&
              iob.latestRequest.id === cb.insertionOrderBudgetRequestId
            ) {
              return this.isAfterFromNow(iob.latestRequest.startTime)
            }
            return false
          })

        if (isStartedAlready) {
          return TemporaryCampaignBudgetFormModel.assign({
            ...cb,
            budget: 0,
          })
        } else {
          return cb
        }
      })
    },
    isAfterFromNow(time) {
      return dayjs().isAfter(dayjs(time).endOf('minute'))
    },
    async isFirstCampaign() {
      const res = await CampaignRepository.search({
        advertiserId: this.insertionOrder.advertiserId,
        insertionOrder: {
          ids: [this.insertionOrder.id],
        },
        campaignStatuses: ['active', 'inactive'],
        pageNo: 1,
      })
      return res.count === 1
    },
    async getAgencyId(advertiserId) {
      const res = await advertiserApi.fetchAdvertiser(advertiserId)
      return res.item.agencyId
    },
    getCreativeManual() {
      if (this.form.campaign.useCreativeSchedule)
        return {
          manualTitle: 'クリエイティブスケジューリング ヘルプはこちら',
          manualUrl:
            'https://go.geologic.co.jp/adminmanual_campaign_creative_scheduling',
        }
      if (
        this.form.campaign.creativeAttachTarget ===
        CreativeAttachTargets.CAMPAIGN
      )
        return {
          manualTitle: 'クリエイティブ設定 ヘルプはこちら',
          manualUrl:
            'https://go.geologic.co.jp/adminmanual_campaign_creativeassing',
        }
      return {
        manualTitle: '配信地点グループ別 クリエイティブ設定 ヘルプはこちら ',
        manualUrl:
          'https://go.geologic.co.jp/adminmanual_campaign_creative_location',
      }
    },
    openHelpPage(url) {
      window.open(url, '_blank', 'noreferrer')
    },
  },
}
</script>
